import React, { useCallback, useMemo, useRef } from "react";
import { debounce } from "lodash";

import { Autocomplete, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { CharAutocomplete } from "components/Characteristics/Deal-form-drawer/CharAutocomplete";
import { CreateAssetClass, CreateSubAssetClass } from "components/Prompts";
import { currenciesArray } from "constants/index";
import { usePrompt } from "hooks";
import { useTaskDealsSelect } from "store";
import { IAssetClass } from "store/types";

import { useDealCreateContext } from "../context/DealCreateContext";
import { NavigationButtons } from "./NavigationButtons";
import { TemplateSelector } from "./TemplateSelector";

export const BasicFormScreen: React.FC = () => {
  const {
    state,
    setState,
    setField,
    goToScreen,
    goBack,
    handleSubmit,
    submitting,
    assetClasses,
    setAssetClasses,
    stages,
    templates,
    setTemplates,
    characteristics,
    geographyCharacteristicId
  } = useDealCreateContext();
  const { dealName, stageId, assetClass, currency, geography, templateId, fieldErrors } = state;

  const openPrompt = usePrompt();
  const deals = useTaskDealsSelect();

  const checkExistingDealTitle = useCallback(
    (title: string) => {
      const trimmedTitle = title.trim();
      const isDuplicate = trimmedTitle && deals.some((deal) => deal.title === trimmedTitle);

      setState((prev) => {
        if (isDuplicate) {
          return {
            ...prev,
            fieldErrors: { ...prev.fieldErrors, title: "A deal with this name already exists" }
          };
        }

        const { title: _, ...rest } = prev.fieldErrors;
        return { ...prev, fieldErrors: rest };
      });
    },
    [deals, setState]
  );

  const debouncedCheckTitle = useMemo(() => debounce(checkExistingDealTitle, 300), [checkExistingDealTitle]);

  const assetClassInputRef = useRef<HTMLInputElement>(null);
  const subAssetClassInputRef = useRef<HTMLInputElement>(null);
  const subAssetClassOptions = assetClass?.sub_asset_class ?? [];

  const selectedStage = useMemo(() => {
    return stages.find((s) => s.id === stageId) || null;
  }, [stages, stageId]);

  const isFileUploadFlow = state.flowPath === "file-upload";

  const selectedStatusId = selectedStage?.status_id;

  const requiredCharacteristics = useMemo(() => {
    if (!selectedStatusId) return [];
    return characteristics.filter((c) => c.required_to_status_ids?.includes(selectedStatusId));
  }, [characteristics, selectedStatusId]);

  const hasRequiredCharacteristics = requiredCharacteristics.length > 0;

  const allRequiredFilled = useMemo(() => {
    if (!hasRequiredCharacteristics) return true;
    return requiredCharacteristics.every((rc) =>
      state.characteristicsData.some((cd) => cd.characteristic_id === rc.id && cd.value)
    );
  }, [requiredCharacteristics, hasRequiredCharacteristics, state.characteristicsData]);

  const basicFieldsMissing = !dealName.trim() || !assetClass || !stageId || !currency;
  const requiredCharsMissing = hasRequiredCharacteristics && !allRequiredFilled;
  const isCreateDisabled = submitting || basicFieldsMissing || requiredCharsMissing;

  const selectAssetClass = (value: IAssetClass | null) => {
    setState((prev) => ({
      ...prev,
      assetClass: value,
      subAssetClass: null,
      templateId: null,
      error: null
    }));
  };

  const createAssetClassHandler = async () => {
    const inputValue = assetClassInputRef.current?.value || "";
    const result = await openPrompt({
      Component: CreateAssetClass,
      props: { initialValue: inputValue }
    });
    if (!result) return;
    setAssetClasses((prev: IAssetClass[] = []) => [...prev, result]);
    selectAssetClass(result);
  };

  const createSubAssetClassHandler = async () => {
    if (!assetClass) return;

    const { id: parentId } = assetClass;
    const inputValue = subAssetClassInputRef.current?.value || "";

    const result = await openPrompt({
      Component: CreateSubAssetClass,
      props: { initialValue: inputValue, parentId }
    });

    if (!result) return;

    // Add to parent asset class's sub_asset_class list
    const updatedAssetClass: IAssetClass = {
      ...assetClass,
      sub_asset_class: [...(assetClass.sub_asset_class || []), result]
    };
    setAssetClasses((prev: IAssetClass[] = []) => prev.map((ac) => (ac.id === parentId ? updatedAssetClass : ac)));
    setState((prev) => ({ ...prev, assetClass: updatedAssetClass, subAssetClass: result }));
  };

  const handleClearFileData = () => {
    setState((prev) => ({
      ...prev,
      file: null,
      validateResult: null,
      flowPath: "manual",
      dealName: "",
      assetClass: null,
      subAssetClass: null,
      currency: "",
      geography: "",
      templateId: null,
      characteristicsData: []
    }));
  };

  return (
    <>
      <Stack sx={{ padding: "32px", gap: "20px", flexGrow: 1, overflowY: "auto" }}>
        {/* Extraction complete banner — file upload flow only */}
        {isFileUploadFlow && state.file && (
          <Stack
            sx={{
              bgcolor: "#f6f8fc",
              borderRadius: "8px",
              width: "100%"
            }}
          >
            <Stack direction="row" alignItems="center" gap="10px" sx={{ padding: "10px" }}>
              <Stack sx={{ gap: "2px", flex: 1 }}>
                <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#071f35" }}>
                  Extraction complete:{" "}
                  <Typography
                    component="span"
                    sx={{ fontSize: "15px", fontWeight: 500, lineHeight: "20px", color: "#071f35" }}
                  >
                    {state.file.name}
                  </Typography>
                </Typography>
                <Typography sx={{ fontSize: "15px", lineHeight: "20px", color: "#496682" }}>
                  The extracted data is added to the fields of the form below.
                </Typography>
              </Stack>
              <Button
                variant="outlined"
                onClick={handleClearFileData}
                sx={{
                  textTransform: "none",
                  borderRadius: "32px",
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#071f35",
                  borderColor: "#7b92a9",
                  bgcolor: "white",
                  px: "8px",
                  py: "4px",
                  maxWidth: "240px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&:hover": { borderColor: "#496682", bgcolor: "#f6f8fc" }
                }}
              >
                Clear file and data
              </Button>
            </Stack>
          </Stack>
        )}

        {/* Form fields */}
        <Stack sx={{ gap: "20px" }}>
          {/* Row 1: Deal Name + Deal Stage */}
          <Stack direction="row" gap="20px">
            <TextField
              label="Deal Name"
              placeholder="Enter Deal Name"
              required
              fullWidth
              value={dealName}
              onChange={(e) => {
                const newValue = e.target.value;
                setField("dealName", newValue);
                debouncedCheckTitle(newValue);
              }}
              error={!!fieldErrors.title}
              helperText={fieldErrors.title}
            />
            <Autocomplete
              fullWidth
              value={selectedStage}
              onChange={(_, value) => setField("stageId", value?.id ?? null)}
              options={stages}
              getOptionLabel={(option) => option.title}
              renderInput={(params) => (
                <TextField {...params} label="Deal Stage" placeholder="Select Deal Stage" required />
              )}
            />
          </Stack>

          {/* Row 2: Asset Class + Sub Asset Class */}
          <Stack direction="row" gap="20px">
            <Autocomplete
              fullWidth
              onChange={(_, value) => {
                selectAssetClass(value);
                if (fieldErrors.asset_class_id) {
                  setState((prev) => {
                    const { asset_class_id: _, ...rest } = prev.fieldErrors;
                    return { ...prev, fieldErrors: rest };
                  });
                }
              }}
              getOptionLabel={(option) => option.account_alias || option.name}
              options={assetClasses}
              value={assetClass}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography>{option.account_alias || option.name}</Typography>
                </li>
              )}
              slotProps={{
                paper: {
                  component: ({ children }) => (
                    <Paper
                      sx={{
                        position: "absolute",
                        backgroundColor: "white",
                        width: "100%",
                        zIndex: "10",
                        borderRadius: "10px",
                        "& .MuiAutocomplete-option:hover": { backgroundColor: "#edf1f9" }
                      }}
                    >
                      {children}
                      <Button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          createAssetClassHandler();
                        }}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          height: "50px",
                          backgroundColor: "#fff",
                          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                          borderRadius: "0 0 10px 10px",
                          zIndex: 100
                        }}
                        fullWidth
                      >
                        Add Asset Class
                      </Button>
                    </Paper>
                  )
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={assetClassInputRef}
                  label="Asset Class"
                  placeholder="Select Asset Class"
                  error={!!fieldErrors.asset_class_id}
                  helperText={fieldErrors.asset_class_id}
                  required
                />
              )}
            />
            <Autocomplete
              fullWidth
              onChange={(_, value) => {
                setField("subAssetClass", value);
                if (fieldErrors.sub_asset_class_id) {
                  setState((prev) => {
                    const { sub_asset_class_id: _, ...rest } = prev.fieldErrors;
                    return { ...prev, fieldErrors: rest };
                  });
                }
              }}
              options={subAssetClassOptions}
              value={state.subAssetClass}
              getOptionLabel={(option) => option.account_alias || option.name}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography>{option.account_alias || option.name}</Typography>
                </li>
              )}
              slotProps={{
                paper: {
                  component: ({ children }) => (
                    <Paper
                      sx={{
                        position: "absolute",
                        backgroundColor: "white",
                        width: "100%",
                        zIndex: "10",
                        borderRadius: "10px",
                        "& .MuiAutocomplete-option:hover": { backgroundColor: "#edf1f9" }
                      }}
                    >
                      {children}
                      <Button
                        disabled={!assetClass}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          createSubAssetClassHandler();
                        }}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          height: "50px",
                          backgroundColor: "#fff",
                          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                          borderRadius: "0 0 10px 10px",
                          zIndex: 100
                        }}
                        fullWidth
                      >
                        Add Sub Asset Class
                      </Button>
                    </Paper>
                  )
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sub Asset Class"
                  placeholder="Choose Sub Asset Class"
                  inputRef={subAssetClassInputRef}
                  error={!!fieldErrors.sub_asset_class_id}
                  helperText={fieldErrors.sub_asset_class_id}
                />
              )}
            />
          </Stack>

          {/* Row 3: Currency + Geography */}
          <Stack direction="row" gap="20px">
            <Autocomplete
              fullWidth
              value={currency}
              onChange={(_, value) => setField("currency", value || "")}
              options={currenciesArray}
              renderInput={(params) => (
                <TextField {...params} label="Currency" placeholder="Select Currency" required />
              )}
            />
            {geographyCharacteristicId ? (
              <CharAutocomplete
                charName="Geography"
                placeholder="Search or Enter Geography"
                characteristicId={geographyCharacteristicId}
                value={geography}
                onChange={(value) => setField("geography", value)}
              />
            ) : (
              <TextField
                fullWidth
                label="Geography"
                placeholder="Enter Geography"
                value={geography}
                onChange={(e) => setField("geography", e.target.value)}
              />
            )}
          </Stack>
        </Stack>

        {/* Template Selection */}
        {!!templates.length && (
          <TemplateSelector
            templates={templates}
            selectedTemplateId={templateId}
            onSelect={(id) => setField("templateId", id)}
            onTemplatesChanged={setTemplates}
            assetClassId={assetClass?.id}
          />
        )}
      </Stack>

      <NavigationButtons
        showBack={state.flowPath !== null || state.screenHistory.length > 1}
        onBack={goBack}
        onSecondary={() => goToScreen("characteristics")}
        secondaryLabel="More Information"
        onNext={handleSubmit}
        nextLabel="Create Deal"
        nextDisabled={isCreateDisabled}
        {...(requiredCharsMissing && {
          nextTooltip: "Complete required fields in\n'More Information' to create a deal."
        })}
        {...(isFileUploadFlow &&
          !hasRequiredCharacteristics && {
            secondaryTooltip:
              "Deal will be created with current fields. Open 'More Information' to review AI-extracted details."
          })}
      />
    </>
  );
};
